'use client';

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';
import useOrganizationStore, { getQueryClient } from '@/state/orgState';
import useUserStore from '@/state/userState';
import { createTeam } from '@/apis/createTeam';
import {
  useAddUserToTeam,
  useRemoveUserFromTeam,
} from '@/hooks/useTeamData';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  fetchOrgMembers,
  ORG_MEMBERS_FIRST_LIMIT,
} from '@/apis/fetchOrgMembers';
import { useDebounce } from '@/hooks/useDebounce';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import User from '@/types/userType';
import { useUserLogged } from '@/hooks/useUserLogged';
import { Checkbox } from '@/components/ui/checkbox';
import posthog from '@/lib/posthog';
import Image from 'next/image';

interface TeamTypeWithMembers {
  TeamMembers: any[];
  TeamName: string;
  TeamUUID?: string;
}

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  teams: TeamTypeWithMembers[];
  setTeams: (teams: TeamTypeWithMembers[]) => void;
  userToAssign?: User | null;
  /** Pre-fills the team name field on open, e.g. what the caller already
   * typed into an inline "Create new team <name>" shortcut before this
   * modal took over. */
  initialTeamName?: string;
  onTeamCreated?: (teamUUID: string) => void;
}

function CreateTeamModalHeader() {
  return (
    <DialogHeader className="border-b border-ds-border-subtlest pb-3 flex-shrink-0">
      <DialogTitle className="text-xl font-semibold text-ds-text-darker">
        Create New Team
      </DialogTitle>
    </DialogHeader>
  );
}

// Helper functions for user display. Hoisted to module scope: these are pure
// functions of their `user` argument, so declaring them inside the component
// rebuilds them on every render for no benefit (react-doctor
// prefer-module-scope-pure-function).
const getUserDisplayName = (user: User) => {
  if (
    user.FirstName &&
    user.FirstName !== 'null' &&
    user.FirstName !== 'undefined' &&
    user.FirstName !== user.GithubUsername
  ) {
    return `${user.FirstName} ${user.LastName && user.LastName !== 'null' && user.LastName !== 'undefined' ? user.LastName : ''}`.trim();
  }
  return user.GithubUsername || user.GitlabUsername || 'Unknown User';
};

const getUserUsername = (user: User) => {
  return user.GithubUsername || user.GitlabUsername || user.UserUUID;
};

export const CreateTeamModal = ({
  isOpen,
  onClose,
  teams,
  setTeams,
  userToAssign,
  initialTeamName,
  onTeamCreated,
}: CreateTeamModalProps) => {
  const toast = useToast();
  const [teamName, setTeamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState<{
    // Keyed by UUID and holding the whole row, not just the id. The picker now
    // shows one server-side search window rather than the full roster, so a
    // selected person is routinely absent from the rows currently loaded.
    // Looking their details back up in that window would drop them from the
    // chips and, worse, from the submit payload.
    selectedUsers: Map<string, User>;
    teamUUID: string | null;
    existingMembers: User[];
  }>({
    selectedUsers: new Map(),
    teamUUID: null,
    existingMembers: [],
  });
  const { selectedUsers, teamUUID, existingMembers } = modalState;
  const setSelectedUsers = (
    v: Map<string, User> | ((prev: Map<string, User>) => Map<string, User>)
  ) => {
    setModalState((prev) => ({
      ...prev,
      selectedUsers: typeof v === 'function' ? v(prev.selectedUsers) : v,
    }));
  };
  const setTeamUUID = (v: string | null) => {
    setModalState((prev) => ({ ...prev, teamUUID: v }));
  };
  const setExistingMembers = (v: User[]) => {
    setModalState((prev) => ({ ...prev, existingMembers: v }));
  };
  const [searchQuery, setSearchQuery] = useState('');

  const organization = useOrganizationStore((state) => state.organization);
  const { user } = useUserLogged();
  const addUserToTeamMutation = useAddUserToTeam(organization?.OrgUUID);
  const removeUserFromTeamMutation = useRemoveUserFromTeam(
    organization?.OrgUUID
  );

  // Memoized header object for API calls
  const headerObj = useMemo(() => {
    const user = useUserStore.getState().user;
    const organization = useOrganizationStore.getState().organization;

    return {
      ...emptyHeaderObj,
      Useruuid: user?.UserUUID,
      Useremail: user?.Email,
      Username: user?.FirstName,
      Orgname: organization?.OrgName,
      Orguuid: organization?.OrgUUID,
    };
  }, []);

  /*
   * Candidates for the picker, one server-searched window at a time.
   *
   * This used to load the org's entire roster (~600KB at 1,350 members) and
   * filter it in the browser, and because the modal is rendered while closed it
   * did so on every page mount. Now it fetches only when open, and the search
   * box queries the database, so the picker reaches people who were never in a
   * loaded window.
   */
  const debouncedUserSearch = useDebounce(searchQuery, 300);
  const { data: candidateWindow, isLoading: isLoadingUsers } = useQuery({
    queryKey: [
      'orgMembers',
      'picker',
      organization?.OrgUUID,
      debouncedUserSearch,
    ],
    queryFn: () =>
      fetchOrgMembers({
        offset: 0,
        limit: ORG_MEMBERS_FIRST_LIMIT,
        search: debouncedUserSearch,
      }),
    enabled: isOpen && !!organization?.OrgUUID,
    staleTime: 60 * 1000,
    // Keeps the list in place while a new term loads instead of emptying it.
    placeholderData: keepPreviousData,
  });
  const candidatesTruncated = candidateWindow?.has_more ?? false;

  // Fetch existing team members when teamUUID is set
  useEffect(() => {
    // Guards the awaits below: a close-then-reopen or a mid-flight teamUUID
    // change can let a superseded run land its members after a newer one, so
    // every setModalState checks `cancelled` first (react-doctor
    // no-set-state-after-await-in-effect).
    let cancelled = false;

    const fetchExistingMembers = async () => {
      if (teamUUID && organization?.OrgUUID) {
        try {
          const { fetchTeamMembers } = await import('@/apis/fetchTeamMembers');
          const result = await fetchTeamMembers({
            TeamUUID: teamUUID,
            OrgUUID: organization.OrgUUID,
          });
          if (result?.Members && !cancelled) {
            setModalState((prev) => ({
              ...prev,
              existingMembers: result.Members,
            }));
          }
        } catch (error) {}
      } else if (!cancelled) {
        setModalState((prev) => ({ ...prev, existingMembers: [] }));
      }
    };

    fetchExistingMembers();

    return () => {
      cancelled = true;
    };
  }, [teamUUID, organization?.OrgUUID]);

  // Reset (or re-default from userToAssign) when open state or userToAssign
  // changes. Adjusted during render rather than in an effect: an effect here
  // would let React paint one frame with the previous session's team
  // name/selections/admin before correcting them (react-doctor
  // no-adjust-state-on-prop-change). This is React's own documented pattern
  // for this exact case:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevOpenProps, setPrevOpenProps] = useState({
    isOpen,
    userToAssign,
    initialTeamName,
  });
  if (
    prevOpenProps.isOpen !== isOpen ||
    prevOpenProps.userToAssign !== userToAssign ||
    prevOpenProps.initialTeamName !== initialTeamName
  ) {
    setPrevOpenProps({ isOpen, userToAssign, initialTeamName });
    if (!isOpen) {
      setTeamName('');
      setModalState({
        selectedUsers: new Map(),
        teamUUID: null,
        existingMembers: [],
      });
    } else {
      setTeamName(initialTeamName || '');
      if (userToAssign) {
        setModalState((prev) => ({
          ...prev,
          selectedUsers: new Map([[userToAssign.UserUUID, userToAssign]]),
        }));
      }
    }
  }

  // Only the already-in-team exclusion is left to do here: search now runs in
  // the database, so re-filtering the window by the same term would just hide
  // rows the server already matched (it also searches columns this filter did
  // not, such as the git usernames).
  const availableUsers = useMemo(() => {
    const existingMemberIds = new Set(
      existingMembers.map((member) => member.UserUUID)
    );
    // Read the rows inside the memo: a `?? []` fallback outside it is a new
    // array identity on every render, which defeats the memo entirely.
    return (candidateWindow?.users ?? []).filter(
      (user) => !existingMemberIds.has(user.UserUUID)
    );
  }, [candidateWindow?.users, existingMembers]);

  const handleToggleUser = (user: User) => {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      if (next.has(user.UserUUID)) {
        next.delete(user.UserUUID);
      } else {
        next.set(user.UserUUID, user);
      }
      return next;
    });
  };

  const handleCreateTeam = async () => {
    // Validate team name
    if (!teamName.trim()) {
      toast({
        title: 'Team name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (teamName.trim().length < 2) {
      toast({
        title: 'Team name must be at least 2 characters long',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (teamName.trim().length > 50) {
      toast({
        title: 'Team name must be less than 50 characters',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Check if team name already exists
    const existingTeam = teams.find(
      (team) => team.TeamName.toLowerCase() === teamName.trim().toLowerCase()
    );
    if (existingTeam) {
      toast({
        title: 'Team name already exists',
        description: 'Please choose a different team name',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);

      posthog.capture('team_created', {
        team_name: teamName.trim(),
        organization_uuid: organization?.OrgUUID,
        organization_name: organization?.OrgName,
        github_username: user?.GithubUsername,
      });

      // Create team first
      const data = await createTeam({
        OrgUUID: organization?.OrgUUID || '',
        TeamName: teamName.trim(),
      });

      // Parse response if it's a string
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (parseError) {
          toast({
            title: 'Error creating team',
            description: 'Invalid response from server',
            status: 'error',
            duration: 3000,
          });
          setIsLoading(false);
          return;
        }
      }

      // Check for any errors in the response
      if (parsedData?.Error) {
        toast({
          title: 'Error creating team',
          description: parsedData.Error,
          status: 'error',
          duration: 3000,
        });
        setIsLoading(false);
        return;
      }

      const newTeamUUID =
        parsedData?.TeamUUID || parsedData?.teamUUID || parsedData?.uuid;
      if (!newTeamUUID) {
        toast({
          title: 'Error creating team',
          description: 'Failed to create team - no TeamUUID returned',
          status: 'error',
          duration: 5000,
        });
        setIsLoading(false);
        return;
      }

      // Add selected users to the team after creation
      if (selectedUsers.size > 0) {
        try {
          const usersToAdd = Array.from(selectedUsers.values());

          // Process each user: remove from current team if needed, then add to new team
          const addPromises = usersToAdd.map(async (userToAdd) => {
            // Which team the user is currently in, read from their own
            // TeamUUID. Scanning each team's member list by git username missed
            // anyone whose provider records did not line up, and a missed match
            // skipped the remove step below and left them on two teams. It also
            // fails outright for callers whose teams carry no member lists.
            const currentTeam = userToAdd.TeamUUID
              ? teams.find((team) => team.TeamUUID === userToAdd.TeamUUID)
              : undefined;

            // If user is in a different team, remove them first
            if (
              currentTeam &&
              currentTeam.TeamUUID &&
              currentTeam.TeamUUID !== newTeamUUID
            ) {
              await removeUserFromTeamMutation.mutateAsync({
                teamId: currentTeam.TeamUUID,
                userId: userToAdd.UserUUID,
              });
            }

            // Add user to the new team
            await addUserToTeamMutation.mutateAsync({
              teamId: newTeamUUID,
              userId: userToAdd.UserUUID,
            });
          });

          await Promise.all(addPromises);

          // Show team creation success toast
          toast({
            title: `Team ${teamName} created successfully`,
            status: 'success',
            duration: 3000,
          });
        } catch (addError: any) {
          // Team was created but adding members failed
          toast({
            title: `Team ${teamName} created successfully`,
            status: 'success',
            duration: 3000,
          });
          toast({
            title: 'Failed to add some members',
            description:
              addError.message || 'Some members may not have been added',
            status: 'warning',
            duration: 5000,
          });
        }
      } else {
        toast({
          title: `Team ${teamName} created successfully`,
          status: 'success',
          duration: 3000,
        });
      }

      // Invalidate queries
      const queryClient = getQueryClient();
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });

      // Call callback if provided
      if (onTeamCreated) {
        onTeamCreated(newTeamUUID);
      }

      // Update teams list (only if team doesn't already exist)
      const teamExists = teams.some((t) => t.TeamUUID === newTeamUUID);
      if (!teamExists) {
        setTeams([
          ...teams,
          {
            TeamMembers: [],
            TeamName: teamName.trim(),
            TeamUUID: newTeamUUID,
          },
        ]);
      }

      // Close modal
      onClose();
    } catch (error: any) {
      // Only show error if team creation failed, not if member addition failed
      // (member addition errors are handled in the inner try-catch)
      if (
        !error.message?.includes('add') &&
        !error.message?.includes('member')
      ) {
        toast({
          title: 'Error creating team',
          description: error.message || 'Please try again later',
          status: 'error',
          duration: 3000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col bg-ds-surface-white border-ds-border-subtlest">
        <CreateTeamModalHeader />

        <div className="space-y-4 py-3 flex-1 min-h-0">
          {/* Team Name Section */}
          <div className="space-y-1.5">
            <label
              htmlFor="create-team-name"
              className="block text-sm font-medium text-ds-text-default"
            >
              Team name
            </label>
            <input
              id="create-team-name"
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Frontend Team"
              className="w-full px-3 py-2 bg-ds-surface-white border border-ds-border-subtler rounded-md text-ds-text-darker placeholder:text-ds-text-subtle focus:outline-none focus:ring-2 focus:ring-ds-brand-default focus:border-transparent"
            />
          </div>

          {/* Selected Team Members Section */}
          {selectedUsers.size > 0 && (
            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-ds-text-default">
                Users in {teamName || 'team'}:
              </span>
              <div className="flex flex-wrap gap-2 px-3 py-2 bg-ds-gray-subtlest rounded-lg border border-ds-border-subtlest">
                {Array.from(selectedUsers.values()).map((member) => (
                    <div
                      key={member.UserUUID}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-ds-surface-off-white flex items-center justify-center">
                        {member.Image ? (
                          <Image
                            src={member.Image}
                            alt={getUserDisplayName(member)}
                            width={40}
                            height={40}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium text-ds-text-subtle">
                            {getUserDisplayName(member).charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-ds-text-darker text-center">
                        {member.FirstName &&
                        member.FirstName !== 'null' &&
                        member.FirstName !== 'undefined'
                          ? member.FirstName
                          : getUserDisplayName(member)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Existing Team Members Section */}
          {teamUUID && existingMembers.length > 0 && (
            <div className="space-y-1.5">
              <span className="block text-sm font-medium text-ds-text-default">
                User in &apos;{teamName}&apos;:
              </span>
              <div className="flex flex-wrap gap-2 px-3 py-2 bg-ds-gray-subtlest rounded-lg border border-ds-border-subtlest">
                {existingMembers.map((member) => (
                  <div
                    key={member.UserUUID}
                    className="flex flex-col items-center gap-1"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-ds-surface-off-white flex items-center justify-center">
                      {member.Image ? (
                        <Image
                          src={member.Image}
                          alt={getUserDisplayName(member)}
                          width={40}
                          height={40}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-medium text-ds-text-subtle">
                          {getUserDisplayName(member).charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-ds-text-darker text-center">
                      {member.FirstName &&
                      member.FirstName !== 'null' &&
                      member.FirstName !== 'undefined'
                        ? member.FirstName
                        : getUserDisplayName(member)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Members Section */}
          <div className="space-y-3">
            <label
              htmlFor="create-team-add-members-search"
              className="block text-sm font-medium text-ds-text-default"
            >
              Add members
            </label>
            {/* Search Bar */}
            <div className="relative">
              <input
                id="create-team-add-members-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Users"
                className="w-full pl-10 pr-4 py-2 bg-ds-surface-white border border-ds-border-subtler rounded-md text-ds-text-darker placeholder:text-ds-text-subtle focus:outline-none focus:ring-2 focus:ring-ds-brand-default focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-ds-text-subtler"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {isLoadingUsers ? (
              <div className="text-center py-8 text-ds-text-subtle">
                Loading users...
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="text-center py-8 text-ds-text-subtle">
                No users available to add
              </div>
            ) : (
              <div className="border border-ds-border-subtlest rounded-lg overflow-hidden">
                <div
                  className={`overflow-x-auto overflow-y-auto ${selectedUsers.size > 0 ? 'max-h-[250px]' : 'max-h-[400px]'}`}
                >
                  <table className="w-full">
                    <thead className="bg-ds-gray-subtlest sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-3 font-medium text-ds-text-darker text-sm">
                          Users
                        </th>
                        <th className="text-left p-3 font-medium text-ds-text-darker text-sm">
                          Email
                        </th>
                        <th className="text-left p-3 font-medium text-ds-text-darker text-sm">
                          Role
                        </th>
                        <th className="w-12 p-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ds-border-subtlest">
                      {availableUsers.map((user) => {
                        const isSelected = selectedUsers.has(user.UserUUID);

                        return (
                          <tr
                            key={user.UserUUID}
                            className="hover:bg-ds-gray-subtlest transition-colors"
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full overflow-hidden bg-ds-surface-off-white flex items-center justify-center flex-shrink-0">
                                  {user.Image ? (
                                    <Image
                                      src={user.Image}
                                      alt={getUserDisplayName(user)}
                                      width={32}
                                      height={32}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span className="text-xs font-medium text-ds-text-subtle">
                                      {getUserDisplayName(user)
                                        .charAt(0)
                                        .toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-ds-text-darker">
                                    {getUserDisplayName(user)}
                                  </div>
                                  <div className="text-xs text-ds-text-subtle">
                                    {getUserUsername(user)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-sm text-ds-text-darker">
                              {user.Email}
                            </td>
                            <td className="p-3">
                              <div className="text-sm text-ds-text-darker capitalize">
                                {user.Role === 'admin'
                                  ? 'Admin'
                                  : user.Role === 'member'
                                    ? 'Member'
                                    : user.Role || 'Member'}
                              </div>
                            </td>
                            <td className="p-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() =>
                                  handleToggleUser(user)
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* The window is capped, so say so. Silence here would read as
                    "that person is not in this workspace". */}
                {candidatesTruncated && (
                  <p className="border-t border-ds-border-subtlest p-3 text-center text-sm text-ds-text-subtle">
                    Showing the first {ORG_MEMBERS_FIRST_LIMIT} matches. Type a
                    name or email to narrow it down.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-3 border-t border-ds-border-subtlest flex-shrink-0">
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={handleCreateTeam}
            disabled={isLoading || !teamName.trim()}
            className="font-medium px-6"
          >
            {isLoading ? 'Creating...' : 'Create team'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
